import type {
    GmailFullMessageResponse,
    GmailMessageListResponse,
    GmailMessagePayloadPartBody,
    GoogleUser,
    ListGmailMessagesParams,
    DriveFile,
    GoogleSheetsSpreadsheet,
    GoogleSheetsValueRange,
    GoogleSheetsBatchUpdateRequest,
    GoogleSheetsBatchUpdateResponse,
} from '~common/google-api/types'
import * as api from '~common/google-api/gmail'
import type { StorageModules } from '~/storage/types'
import type { Functions } from 'firebase/functions'
import { httpsCallable } from 'firebase/functions'
import * as googleDriveApi from '~common/google-api/google-drive'
export interface GoogleApiServiceDeps {
    storageModules: StorageModules
    services: {
        functions: Functions
    }
}

export class GoogleApiService {
    private tokensByEmail: Map<string, string> = new Map()
    private activeEmail: string | null = null
    private primaryAccessToken: string | null = null

    constructor(private readonly deps: GoogleApiServiceDeps) {}

    setToken(email: string, accessToken: string): void {
        this.tokensByEmail.set(email, accessToken)
    }

    setActiveEmail(email: string | null): void {
        this.activeEmail = email
    }

    initializePrimary(accessToken: string): void {
        this.primaryAccessToken = accessToken
    }

    getAccessToken(email?: string): string | null {
        if (email) {
            return this.tokensByEmail.get(email) ?? null
        }
        if (this.activeEmail) {
            return this.tokensByEmail.get(this.activeEmail) ?? null
        }
        return null
    }

    getPrimaryAccessToken(): string | null {
        return this.primaryAccessToken
    }

    async listGmailMessages(
        params: ListGmailMessagesParams & { accountEmail?: string },
    ): Promise<GmailMessageListResponse> {
        const token = this.getAccessToken(params.accountEmail)
        if (!token) {
            throw new Error('Access token not initialized')
        }
        return api.listGmailMessages(token, params)
    }

    async getGmailMessage(params: {
        messageId: string
        accountEmail?: string
    }): Promise<GmailFullMessageResponse> {
        const token = this.getAccessToken(params.accountEmail)
        if (!token) {
            throw new Error('Access token not initialized')
        }
        return api.getGmailMessage(token, params)
    }

    async downloadGmailAttachment(params: {
        messageId: string
        attachmentId: string
        accountEmail?: string
    }): Promise<GmailMessagePayloadPartBody> {
        const token = this.getAccessToken(params.accountEmail)
        if (!token) {
            throw new Error('Access token not initialized')
        }
        return api.downloadGmailAttachment(token, params)
    }

    async getUserProfile(accountEmail?: string): Promise<GoogleUser> {
        const token = this.getAccessToken(accountEmail)
        if (!token) {
            throw new Error('Access token not initialized')
        }
        return api.getUserProfile(token)
    }

    async batchGetGmailMessages(params: {
        messageIds: string[]
        accountEmail?: string
    }): Promise<Array<GmailFullMessageResponse | null>> {
        const token = this.getAccessToken(params.accountEmail)
        if (!token) {
            throw new Error('Access token not initialized')
        }
        return api.batchGetGmailMessages(token, params)
    }

    async uploadToExternalStorage(params: {
        filename: string
        data: Blob | ArrayBuffer
        date: string // ISO date string
        fileId?: string // Pre-generated file ID
    }): Promise<DriveFile> {
        const fileData = await this.blobOrArrayBufferToBase64(params.data)

        const saveFileToGoogleDrive = httpsCallable<
            {
                date: string
                fileName?: string
                fileData: string
                fileId?: string
            },
            { fileId: string }
        >(this.deps.services.functions, 'saveFileToGoogleDriveFn')

        const dataToSave = {
            date: params.date,
            fileName: params.filename,
            fileData,
            fileId: params.fileId,
        }

        const saveFileToQonto = httpsCallable<
            {
                date: string
                fileName?: string
                fileData: string
            },
            { fileId: string | null }
        >(this.deps.services.functions, 'saveFileToQontoFn')

        const settings = await this.deps.storageModules.auth
            .getCurrentUser()
            .then((u) => u?.settings)
        const qontoEnabled =
            settings?.plugins?.['sync-to-qonto']?.enabled ?? false

        const promises = [saveFileToGoogleDrive(dataToSave)] as const

        if (qontoEnabled) {
            ;(promises as unknown as Promise<unknown>[]).push(
                saveFileToQonto({
                    date: params.date,
                    fileName: params.filename,
                    fileData,
                }).catch((err) => {
                    console.error('Qonto upload failed', err)
                    return null
                }),
            )
        }

        const [result] = await Promise.all(promises)

        return {
            id: result.data.fileId,
            name: params.filename,
            mimeType: 'application/pdf',
        }
    }

    private async blobOrArrayBufferToBase64(
        data: Blob | ArrayBuffer,
    ): Promise<string> {
        let arrayBuffer: ArrayBuffer

        if (data instanceof Blob) {
            arrayBuffer = await data.arrayBuffer()
        } else {
            arrayBuffer = data
        }

        const bytes = new Uint8Array(arrayBuffer)
        let binary = ''
        const len = bytes.byteLength
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i])
        }
        return btoa(binary)
    }

    async getGoogleDriveFileUrl(fileOrFolderId: string): Promise<string> {
        if (!this.primaryAccessToken) {
            throw new Error('Primary access token not initialized')
        }

        const folder = await googleDriveApi.getDriveFileById(
            this.primaryAccessToken,
            fileOrFolderId,
        )

        if (!folder.webViewLink) {
            throw new Error('Folder does not have a web view link')
        }

        return folder.webViewLink
    }

    async downloadGoogleDriveFile(
        fileId: string,
    ): Promise<{ data: Blob; mimeType: string; filename: string }> {
        if (!this.primaryAccessToken) {
            throw new Error('Primary access token not initialized')
        }
        return googleDriveApi.downloadDriveFile(this.primaryAccessToken, fileId)
    }

    async createGoogleSheetsSpreadsheet(params: {
        title: string
        parentId?: string
    }): Promise<GoogleSheetsSpreadsheet> {
        if (!this.primaryAccessToken) {
            throw new Error('Primary access token not initialized')
        }
        return googleDriveApi.createGoogleSheetsSpreadsheet(
            this.primaryAccessToken,
            params,
        )
    }

    async batchUpdateGoogleSheets(
        spreadsheetId: string,
        request: GoogleSheetsBatchUpdateRequest,
    ): Promise<GoogleSheetsBatchUpdateResponse> {
        if (!this.primaryAccessToken) {
            throw new Error('Primary access token not initialized')
        }
        return googleDriveApi.batchUpdateGoogleSheets(
            this.primaryAccessToken,
            spreadsheetId,
            request,
        )
    }

    async updateGoogleSheetsValues(
        spreadsheetId: string,
        range: string,
        values: GoogleSheetsValueRange['values'],
    ): Promise<void> {
        if (!this.primaryAccessToken) {
            throw new Error('Primary access token not initialized')
        }
        return googleDriveApi.updateGoogleSheetsValues(
            this.primaryAccessToken,
            spreadsheetId,
            range,
            values,
        )
    }

    async moveFileToFolder(fileId: string, folderId: string): Promise<void> {
        if (!this.primaryAccessToken) {
            throw new Error('Primary access token not initialized')
        }
        return googleDriveApi.moveFileToFolder(
            this.primaryAccessToken,
            fileId,
            folderId,
        )
    }

    async generateDriveFileIds(count: number = 1): Promise<string[]> {
        if (!this.primaryAccessToken) {
            throw new Error('Primary access token not initialized')
        }
        return googleDriveApi.generateDriveFileIds(
            this.primaryAccessToken,
            count,
        )
    }
}
